import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { useCallback, useEffect, useState } from 'react'
import './style.css'

const SERVICES = [
  ['AIStor', '객체 저장소'], ['Polaris · Iceberg', '테이블 카탈로그'],
  ['Spark', '수집·변환'], ['StarRocks', '조회'],
]

function headers(json = false) {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token')
  return { ...(json ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }
}

function peerLabel(peer) {
  return `${peer.hostname || `에이전트 ${peer.peer_id}`} · ${peer.status || '상태 미확인'}`
}

function hardwareLabel(peer) {
  if (!peer?.hardware_seen_at || !peer.cpu_cores || !peer.memory_bytes || !peer.root_disk_bytes) {
    return '실제 사양: 에이전트 보고 대기'
  }
  const gigabytes = bytes => (Number(bytes) / (1024 ** 3)).toFixed(1)
  return `에이전트 보고: ${peer.cpu_cores} vCPU · 메모리 ${gigabytes(peer.memory_bytes)}GiB · 루트 디스크 ${gigabytes(peer.root_disk_bytes)}GiB`
}

export default function LabPilot() {
  const [data, setData] = useState(null)
  const [meshId, setMeshId] = useState('')
  const [agentPeerId, setAgentPeerId] = useState('')
  const [dataPeerId, setDataPeerId] = useState('')
  const [computePeerId, setComputePeerId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const load = useCallback(async (signal) => {
    setBusy(true); setError('')
    try {
      const response = await fetch('/api/labs/pilot', { headers: headers(), credentials: 'include', signal })
      const body = await response.json()
      if (!response.ok) throw Error(body.code || '조회 실패')
      setData(body)
      setMeshId(body.pilot?.mesh_id ? String(body.pilot.mesh_id) : '')
      setAgentPeerId(body.pilot?.agent_peer_id ? String(body.pilot.agent_peer_id) : '')
      setDataPeerId(body.pilot?.data_peer_id ? String(body.pilot.data_peer_id) : '')
      setComputePeerId(body.pilot?.compute_peer_id ? String(body.pilot.compute_peer_id) : '')
    } catch (cause) {
      if (cause.name !== 'AbortError') setError(cause.message)
    } finally { if (!signal?.aborted) setBusy(false) }
  }, [])

  useEffect(() => { const controller = new AbortController(); load(controller.signal); return () => controller.abort() }, [load])

  const save = async (event) => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('')
    try {
      const response = await fetch('/api/labs/pilot', {
        method: 'PUT', headers: headers(true), credentials: 'include',
        body: JSON.stringify({ meshId: meshId ? Number(meshId) : null,
          agentPeerId: agentPeerId ? Number(agentPeerId) : null,
          dataPeerId: dataPeerId ? Number(dataPeerId) : null,
          computePeerId: computePeerId ? Number(computePeerId) : null }),
      })
      const body = await response.json()
      if (!response.ok) throw Error(body.code || '저장 실패')
      setData(current => ({ ...current, pilot: body.pilot }))
      setNotice('구성만 저장했습니다. 서버 생성·구매·설치는 수행하지 않았습니다.')
    } catch (cause) { setError(cause.message) }
    finally { setBusy(false) }
  }

  const selectedPeers = (data?.peers || []).filter(peer => String(peer.mesh_id) === meshId)
  const assigned = { agent: data?.peers?.find(peer => peer.peer_id === Number(data?.pilot?.agent_peer_id)),
    data: data?.peers?.find(peer => peer.peer_id === Number(data?.pilot?.data_peer_id)),
    compute: data?.peers?.find(peer => peer.peer_id === Number(data?.pilot?.compute_peer_id)) }

  return <main className="lab-page">
    <header className="lab-header"><div><h1>데이터레이크 설계</h1><p>저비용 노드 구성과 SkynetMesh 에이전트 등록 상태</p></div>
      <button type="button" onClick={() => load()} disabled={busy}>새로고침</button></header>
    <div className="lab-content">
      <p className="lab-warning">이 화면은 구성 등록 도구입니다. 클라우드 서버를 생성하거나 결제하지 않으며, 서비스 상태는 실제 점검이 연결되기 전까지 ‘미확인’으로 표시합니다.</p>
      {error && <p className="lab-error" role="alert">{error}</p>}
      {notice && <p className="lab-success" role="status">{notice}</p>}
      {data?.iwinvPilot && <section className="lab-panel"><h2>선택한 실습안 · IWINV 우분투 한 대</h2>
        <p>리눅스 VDI · {data.iwinvPilot.cpu} vCPU · 메모리 {data.iwinvPilot.memoryGb}GB · 저장공간 {data.iwinvPilot.diskGb}GB</p>
        <p>기준 요금: 하루 {data.iwinvPilot.dailyKrw.toLocaleString('ko-KR')}원 · 월 {data.iwinvPilot.monthlyKrw.toLocaleString('ko-KR')}원</p>
        <p className="lab-muted">{data.iwinvPilot.priceCheckedAt} 계정 상품 조회 기준, 세금·추가 자원 별도. 이 견적 카드는 실제 구매·설치 상태를 조회하지 않습니다. 한 대에 여러 역할을 배정해 순차 검증하며 동시 구동 성능은 미확인입니다.</p>
      </section>}
      <section className="lab-panel"><h2>예산별 시험안 · 구매 전 비교</h2>
        <div className="lab-cards">{(data?.labOptions || []).map(option => <article className="lab-node" key={option.nodeCount}>
          <div className="lab-node-title"><strong>{option.label}</strong><span>{option.nodeCount}대</span></div>
          <p>{option.models}</p><p>월 기본 €{option.monthlyEur.toFixed(2)}</p>
          <p className="lab-muted">{option.limitation}</p>
        </article>)}</div>
        <p className="lab-muted">모두 계정 API로 확인한 2026-10-27 기준 주문 가능 사양의 구매 전 견적입니다. VAT·IPv4·백업·볼륨은 별도이며, 이 화면은 구매를 실행하지 않습니다.</p>
      </section>
      <section className="lab-panel"><h2>소형 실습 구성 · 최대 3대 · 구매 전 대안</h2>
        <div className="lab-cards">{(data?.nodes || []).map(node => <article className="lab-node" key={node.role}>
          <div className="lab-node-title"><strong>{node.label}</strong><span>{node.model}</span></div>
          <p>계획 사양: {node.cpu} vCPU · 메모리 {node.memoryGb}GB · 저장공간 {node.diskGb}GB</p>
          <p>기본 월 €{node.monthlyEur.toFixed(2)}</p>
          <p>에이전트: {assigned[node.role] ? peerLabel(assigned[node.role]) : '미연결'}</p>
          <p>{hardwareLabel(assigned[node.role])}</p>
        </article>)}</div>
        <p className="lab-cost">3대안 월 기본 예상 €{data?.estimatedMonthlyEur?.toFixed(2) || '140.47'} <small>계정 API 확인 {data?.priceCheckedAt || '2026-10-27'} · 생성 전 재고·가격 재확인 필요 · VAT·IPv4·백업·볼륨 별도 · 실제 사양은 구매 후 확인</small></p>
        <p className="lab-muted">기존 CX33·CX43은 현재 주문 불가라 주문 가능한 CPX 대안을 표시합니다. 구매·생성은 아직 하지 않았습니다. AIStor Free는 별도 라이선스가 필요하며, StarRocks를 포함한 전체 서비스를 동시에 띄우는 성능은 미검증입니다. 서버를 중지해도 과금이 계속되므로 실습 종료 후 삭제해야 합니다.</p>
      </section>
      <section className="lab-panel"><h2>구성 등록</h2>
        <form onSubmit={save} className="lab-form">
          <label>연결할 VPC<select value={meshId} onChange={event => { setMeshId(event.target.value); setAgentPeerId(''); setDataPeerId(''); setComputePeerId('') }}>
            <option value="">아직 지정하지 않음</option>{data?.meshes?.map(mesh => <option key={mesh.mesh_id} value={mesh.mesh_id}>{mesh.mesh_name}</option>)}
          </select></label>
          <label>관리 에이전트<select value={agentPeerId} onChange={event => setAgentPeerId(event.target.value)} disabled={!meshId}>
            <option value="">미연결</option>{selectedPeers.map(peer => <option key={peer.peer_id} value={peer.peer_id}>{peerLabel(peer)}</option>)}
          </select></label>
          <label>데이터 노드 에이전트<select value={dataPeerId} onChange={event => setDataPeerId(event.target.value)} disabled={!meshId}>
            <option value="">미연결</option>{selectedPeers.map(peer => <option key={peer.peer_id} value={peer.peer_id}>{peerLabel(peer)}</option>)}
          </select></label>
          <label>StarRocks 노드 에이전트<select value={computePeerId} onChange={event => setComputePeerId(event.target.value)} disabled={!meshId}>
            <option value="">미연결</option>{selectedPeers.map(peer => <option key={peer.peer_id} value={peer.peer_id}>{peerLabel(peer)}</option>)}
          </select></label>
          <button type="submit" disabled={busy || !data}>{busy ? '처리 중' : '구성 저장'}</button>
        </form>
        <p className="lab-muted">한 대로 순차 실습할 때 같은 에이전트를 여러 역할에 지정할 수 있습니다. 실제 서비스 설치·동시 구동 여부는 별도로 점검해야 합니다. 등록 VPC가 없으면 먼저 <a href="/network">네트워크</a>에서 VPC와 에이전트를 준비하세요.</p>
      </section>
      <section className="lab-panel"><h2>서비스 점검</h2><p className="lab-muted">검증 목표: AIStor에 객체 저장 → Polaris에서 Iceberg 카탈로그 생성 → Spark로 테이블 적재 → StarRocks에서 조회.</p><div className="lab-services">
        {SERVICES.map(([name, role]) => <div key={name}><strong>{name}</strong><span>{role}</span><em>미확인 · 아직 설치/점검 연결 안 됨</em></div>)}
      </div></section>
    </div>
  </main>
}
